import { ScrollArea } from "@arcadeai/design-system/components/ui/atoms/scroll-area";
import type { ToolkitCollection } from "@arcadeai/ui-kit/arcade/arcade";
import { Wrench } from "lucide-react";
import type { UseFormReturn } from "react-hook-form";
import { useShallow } from "zustand/shallow";
import { useArcadeClient } from "@/app/playground/execute/hooks/use-arcade-client";
import { usePlaygroundStore } from "@/app/playground/execute/hooks/use-store";
import { AuthAlert } from "../panel/auth-alert";
import { RequirementsStatus } from "./requirements-status";
import { ToolForm } from "./tool-form";
import { ToolSelector } from "./tool-selector";

type FormPanelProps = {
  form: UseFormReturn<Record<string, unknown>>;
  toolkitCollection: ToolkitCollection | undefined;
  onToolSelection: (selection: {
    selectedToolkits: string[];
    selectedTools: string[];
  }) => void;
  onSubmit: () => void;
  isFormValid: boolean;
  tooltipMessage: string | null;
  showHeader?: boolean;
};

export function FormPanel({
  form,
  toolkitCollection,
  onToolSelection,
  onSubmit,
  isFormValid,
  tooltipMessage,
  showHeader = true,
}: FormPanelProps) {
  const { selectedTool, isLoading, authStatus } = usePlaygroundStore(
    useShallow((state) => ({
      selectedTool: state.selectedTool,
      isLoading: state.isLoading,
      authStatus: state.authStatus,
    }))
  );

  const { startAuthorization } = useArcadeClient();

  if (!selectedTool) {
    return null;
  }

  const showAuthAlert = authStatus === "pending";
  const isExecuteDisabled = isLoading || !isFormValid;

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border border-neutral-300 bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-950">
      {showHeader ? (
        <div className="flex min-h-[46px] items-center gap-2 border-neutral-200 border-b bg-neutral-100 px-4 dark:border-neutral-800/80 dark:bg-neutral-900/80">
          <Wrench className="h-3.5 w-3.5 text-neutral-500 dark:text-neutral-400" />
          <span className="font-medium text-neutral-600 text-xs uppercase tracking-widest dark:text-neutral-300">
            Tool Setup
          </span>
        </div>
      ) : null}
      <ScrollArea className="min-h-0 flex-1 overflow-hidden">
        <div className="space-y-4 px-4 pt-5 pb-2">
          <ToolSelector
            onSelectionChange={onToolSelection}
            selectedTool={selectedTool}
            toolkitCollection={toolkitCollection}
          />
          <RequirementsStatus
            onAuthorize={startAuthorization}
            tool={selectedTool}
          />
          {showAuthAlert ? <AuthAlert /> : null}
        </div>
        <div className="px-4 pb-4">
          <ToolForm
            form={form}
            isExecuteDisabled={isExecuteDisabled}
            isExecuting={isLoading}
            onSubmit={onSubmit}
            tooltipMessage={tooltipMessage}
          />
        </div>
      </ScrollArea>
    </div>
  );
}
